/**
 * MyVibe v0 — identity, not settings.
 *
 * iter-83: pick 2-3 example cards that "feel like you" → backend infers
 * tone + spice + region → persists to user.personalize_defaults.
 * Every future generation auto-applies the vibe unless the user overrides.
 *
 * NOT a settings page. The screens read "this is who you are" not
 * "configure your preferences." 6 curated cards across the voice axes
 * (heartfelt × bold × chaotic × faith × poetic × playful).
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";
import { Toaster } from "../components/ui/sonner";
import { useAuth } from "../lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter-105 — the 7 voice axes the backend reads from sample picks.
// Order matters: this is also the surface order on the read-back chip.
const AXIS_LABELS = {
    tone:          { label: "Tone",          icon: "◐" },
    spice:         { label: "Spice",         icon: "◔" },
    locality:      { label: "Region",        icon: "◑" },
    length:        { label: "Length",        icon: "◓" },
    imagery:       { label: "Imagery",       icon: "◒" },
    vulnerability: { label: "Vulnerability", icon: "○" },
    energy:        { label: "Energy",        icon: "◉" },
};

// 6 curated samples — each tagged with the (tone, spice, region) it implies.
// Picking them = inferring you. No questionnaire.
const SAMPLES = [
    { id: "tender_quiet",    title: "the after-quiet",  body: "i'm fine.\ngenuinely.\nmostly.\nprobably.",                  tone: null,        spice: "mild",     region: "UK", swatch: "linear-gradient(135deg,#3a3147,#1e1a26)" },
    { id: "bold_declarative", title: "the receipt",      body: "i was the one who stayed.\neven when staying\nwas a fight.",  tone: "bold",      spice: "bold",     region: null, swatch: "linear-gradient(135deg,#7a1818,#2a0808)" },
    { id: "chaotic_meme",     title: "the math",         body: "you reply nine hours later\nthen post a selfie immediately 😭", tone: null,        spice: "chaotic",  region: "US", swatch: "linear-gradient(135deg,#d4af6e,#7a5a30)" },
    { id: "faith_quiet",      title: "be still",         body: "be still and know.\n— Ps 46:10",                              tone: "reflective",spice: "mild",     region: null, swatch: "linear-gradient(135deg,#1a2438,#0a0e18)" },
    { id: "poetic_image",     title: "the room",         body: "i am the room\nyou came home to.",                            tone: "tender",    spice: "bold",     region: null, swatch: "linear-gradient(135deg,#3d1d3d,#1a0a1a)" },
    { id: "playful_jab",      title: "the screen time",  body: "lowkey toxic.\nhighkey my favorite person.",                  tone: null,        spice: "unhinged", region: "US", swatch: "linear-gradient(135deg,#d96a2a,#5a2a0a)" },
];

export default function MyVibe() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth() || {};
    const [picked, setPicked] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    // iter-105 — show the user's current 7-axis identity at the top if set.
    const [currentVibe, setCurrentVibe] = useState(null);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/me/vibe`, { withCredentials: true });
                if (!cancelled) setCurrentVibe(data?.personalize_defaults || null);
            } catch {
                /* not signed in or first-time — ignore */
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const toggle = (id) => {
        setPicked((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id);
            else if (next.size < 3) next.add(id);
            else toast.info("pick up to 3 — that's plenty for us to read you");
            return next;
        });
    };

    const save = async () => {
        if (picked.size < 2) {
            toast.info("pick at least 2 — gives us a sharper read");
            return;
        }
        setSubmitting(true);
        try {
            const ids = Array.from(picked);
            await axios.post(`${API}/me/vibe`, { picks: ids }, { withCredentials: true });
            toast.success("vibe locked in. every card from here will feel like you.");
            refresh?.();
            setTimeout(() => navigate("/"), 700);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 401) {
                toast.error("sign in first to lock in your vibe");
                window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { source: "my_vibe" } }));
            } else {
                toast.error(typeof detail === "string" ? detail : "couldn't save — try again");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-app text-app pb-24">
            <Toaster theme="dark" position="bottom-center" />
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                onOpenNotifications={() => navigate("/me")}
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />

            <div className="max-w-md mx-auto px-5 pt-6">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-[10px] tracking-[0.32em] uppercase text-amber-300/85 mb-3">my vibe</div>
                    <h1 className="font-display text-3xl text-app leading-tight">
                        pick the ones that <span className="italic font-serif">sound like you</span>
                    </h1>
                    <p className="mt-3 font-serif italic text-app-2 text-sm leading-relaxed">
                        choose 2 or 3. we'll read between them. every card you make after this will be tuned to <em>you</em>, not the internet.
                    </p>
                </motion.div>

                {/* iter-105 — current 7-axis identity read-back. Only shows once
                    the user has saved at least once. Reads as "this is who you
                    are right now" — pick again any time to refine. */}
                {currentVibe && Object.keys(AXIS_LABELS).some((k) => currentVibe[k]) && (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.5 }}
                        className="mt-6 rounded-2xl border border-amber-300/25 bg-amber-400/5 p-4"
                        data-testid="vibe-current-readback"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] tracking-[0.32em] uppercase text-amber-300/85">your vibe today</span>
                            <span className="text-[10px] tracking-[0.18em] uppercase text-app-3">
                                {Object.keys(AXIS_LABELS).filter((k) => currentVibe[k]).length} of 7
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="vibe-axis-chips">
                            {Object.entries(AXIS_LABELS).map(([key, meta]) => {
                                const value = currentVibe[key];
                                if (!value) return null;
                                return (
                                    <span
                                        key={key}
                                        data-testid={`vibe-axis-${key}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 border border-white/12 font-sans text-[11px] text-app"
                                    >
                                        <span className="text-amber-300/85 text-[12px] leading-none">{meta.icon}</span>
                                        <span className="text-app-3 uppercase tracking-[0.18em] text-[9px]">{meta.label}</span>
                                        <span className="font-serif italic text-app">{value}</span>
                                    </span>
                                );
                            })}
                        </div>
                        <p className="mt-3 font-serif italic text-app-3 text-[11px] leading-snug">
                            pick again below to re-tune. nothing is locked.
                        </p>
                    </motion.div>
                )}

                <div className="mt-7 grid grid-cols-2 gap-3" data-testid="vibe-samples">
                    {SAMPLES.map((s) => {
                        const active = picked.has(s.id);
                        return (
                            <motion.button
                                key={s.id}
                                type="button"
                                whileTap={{ scale: 0.96 }}
                                onClick={() => toggle(s.id)}
                                className={`relative rounded-2xl p-4 text-left transition-all min-h-[180px] flex flex-col justify-between ${
                                    active
                                        ? "ring-2 ring-amber-300/80 shadow-[0_0_28px_-6px_rgba(252,211,77,0.55)]"
                                        : "ring-1 ring-white/10 hover:ring-white/25"
                                }`}
                                style={{ background: s.swatch }}
                                data-testid={`vibe-sample-${s.id}`}
                                aria-pressed={active}
                            >
                                {active && (
                                    <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
                                        <Check className="w-3.5 h-3.5 text-black" strokeWidth={2.5} />
                                    </span>
                                )}
                                <div className="text-[10px] tracking-[0.22em] uppercase text-white/70 font-medium">{s.title}</div>
                                <p className="font-serif italic text-white text-[16px] leading-snug whitespace-pre-line">
                                    {s.body}
                                </p>
                            </motion.button>
                        );
                    })}
                </div>

                <div className="mt-7 flex items-center justify-between text-xs text-app-3">
                    <span data-testid="vibe-pick-count">
                        <span className="text-app-2 font-medium">{picked.size}</span> of 3 picked
                    </span>
                    {!user && (
                        <span className="italic font-serif text-app-3/80">sign in to save</span>
                    )}
                </div>

                <button
                    type="button"
                    disabled={submitting || picked.size < 2}
                    onClick={save}
                    data-testid="vibe-save"
                    className={`mt-3 w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-medium text-sm tracking-wide transition-all ${
                        picked.size >= 2 && !submitting
                            ? "bg-amber-400 hover:bg-amber-300 text-black shadow-[0_0_32px_-6px_rgba(252,211,77,0.5)]"
                            : "bg-white/5 text-white/40 cursor-not-allowed"
                    }`}
                >
                    {submitting ? "reading you..." : (
                        <>
                            <Sparkles className="w-4 h-4" strokeWidth={1.7} />
                            lock in my vibe
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </>
                    )}
                </button>
                <p className="mt-3 text-center text-[10px] tracking-[0.22em] uppercase text-app-3">
                    you can change this anytime
                </p>
            </div>
        </div>
    );
}
